/**
 * Error Utilities
 *
 * Utilities for categorizing and formatting API errors.
 * Supports i18n by returning message keys that can be translated.
 */

// Manual type definition for AxiosError structure (compatible with all axios versions)
interface AxiosErrorLike {
  isAxiosError: boolean;
  code?: string;
  request?: unknown;
  response?: {
    status: number;
    data?: unknown;
  };
}

/**
 * Error category types
 */
export type ErrorCategory = 'network' | 'auth' | 'validation' | 'notFound' | 'server' | 'unknown';

/**
 * Parsed error structure with category, user-friendly message, and optional code
 */
export interface ParsedError {
  category: ErrorCategory;
  message: string;
  messageKey?: string;
  code?: string;
  originalError?: unknown;
}

/**
 * Network error codes that indicate connection issues
 */
const NETWORK_ERROR_CODES = new Set([
  'ECONNABORTED',
  'ERR_NETWORK',
  'ETIMEDOUT',
  'ENOTFOUND',
  'ECONNREFUSED',
  'ECONNRESET',
  'ERR_INTERNET_DISCONNECTED',
]);

/**
 * Default user-friendly messages for each error category
 */
const DEFAULT_MESSAGES: Record<ErrorCategory, string> = {
  network: 'Unable to connect. Please check your internet connection.',
  auth: 'Your session has expired. Please log in again.',
  validation: 'Please check your input and try again.',
  notFound: 'The requested resource was not found.',
  server: 'Something went wrong on our end. Please try again later.',
  unknown: 'An unexpected error occurred. Please try again.',
};

/**
 * i18n message keys for each error category
 */
const MESSAGE_KEYS: Record<ErrorCategory, string> = {
  network: 'errors.network',
  auth: 'errors.auth',
  validation: 'errors.validation',
  notFound: 'errors.notFound',
  server: 'errors.server',
  unknown: 'errors.unknown',
};

/**
 * Check if error is an Axios error
 * Compatible with all axios versions (some older versions don't have isAxiosError)
 */
const isAxiosError = (error: unknown): error is AxiosErrorLike => {
  // Check for isAxiosError property that axios adds to its errors
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    return (error as AxiosErrorLike).isAxiosError === true;
  }
  return false;
};

/**
 * Extract error code from axios error
 */
export const extractErrorCode = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) {
    return undefined;
  }

  // Check for axios error code (network errors)
  if (error.code) {
    return error.code;
  }

  // Check for response data code
  const responseData = error.response?.data as Record<string, unknown> | undefined;
  if (responseData?.code && typeof responseData.code === 'string') {
    return responseData.code;
  }

  return undefined;
};

/**
 * Extract error message from axios error response
 * Looks in common locations: data.message, data.error, data.errors
 */
const extractResponseMessage = (error: AxiosErrorLike): string | undefined => {
  const data = error.response?.data as Record<string, unknown> | undefined;

  if (!data) {
    return undefined;
  }

  // Check for message field
  if (typeof data.message === 'string' && data.message) {
    return data.message;
  }

  // Check for error field
  if (typeof data.error === 'string' && data.error) {
    return data.error;
  }

  // Check for errors array (validation errors)
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const firstError = data.errors[0];
    if (typeof firstError === 'string') {
      return firstError;
    }
    if (typeof firstError === 'object' && firstError !== null) {
      const errorObj = firstError as Record<string, unknown>;
      if (typeof errorObj.message === 'string') {
        return errorObj.message;
      }
      if (typeof errorObj.msg === 'string') {
        return errorObj.msg;
      }
    }
  }

  return undefined;
};

/**
 * Categorize an error based on its type and status code
 */
export const categorizeError = (error: unknown): ErrorCategory => {
  if (!isAxiosError(error)) {
    return 'unknown';
  }

  // Check for network errors (no response received)
  if (!error.response) {
    // Check error code for network-related issues
    if (error.code && NETWORK_ERROR_CODES.has(error.code)) {
      return 'network';
    }

    // No response and request was made - likely network error
    if (error.request) {
      return 'network';
    }

    return 'unknown';
  }

  // Categorize based on HTTP status code
  const status = error.response.status;

  if (status === 401 || status === 403) {
    return 'auth';
  }

  if (status === 400 || status === 422) {
    return 'validation';
  }

  if (status === 404) {
    return 'notFound';
  }

  if (status >= 500) {
    return 'server';
  }

  return 'unknown';
};

/**
 * Get user-friendly message for an error
 * Returns validation message from response for validation errors,
 * or default message for the error category
 */
export const getUserFriendlyMessage = (
  error: unknown,
  category?: ErrorCategory
): string => {
  const errorCategory = category ?? categorizeError(error);

  // For validation errors, try to extract the specific message
  if (errorCategory === 'validation' && isAxiosError(error)) {
    const responseMessage = extractResponseMessage(error);
    if (responseMessage) {
      return responseMessage;
    }
  }

  // Return default message for the category
  return DEFAULT_MESSAGES[errorCategory];
};

/**
 * Get i18n message key for an error category
 */
export const getMessageKey = (category: ErrorCategory): string => {
  return MESSAGE_KEYS[category];
};

/**
 * Parse an error into a structured format with category, message, and code
 * This is the main function for error handling
 */
export const parseError = (error: unknown): ParsedError => {
  const category = categorizeError(error);
  const message = getUserFriendlyMessage(error, category);
  const messageKey = getMessageKey(category);
  const code = extractErrorCode(error);

  return {
    category,
    message,
    messageKey,
    code,
    originalError: error,
  };
};

/**
 * Check if an error is a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  return categorizeError(error) === 'network';
};

/**
 * Check if an error is an authentication error
 */
export const isAuthError = (error: unknown): boolean => {
  return categorizeError(error) === 'auth';
};

/**
 * Check if an error is a validation error
 */
export const isValidationError = (error: unknown): boolean => {
  return categorizeError(error) === 'validation';
};

/**
 * Check if an error is a not found error
 */
export const isNotFoundError = (error: unknown): boolean => {
  return categorizeError(error) === 'notFound';
};

/**
 * Check if an error is a server error
 */
export const isServerError = (error: unknown): boolean => {
  return categorizeError(error) === 'server';
};

/**
 * Get HTTP status code from an error
 */
export const getStatusCode = (error: unknown): number | undefined => {
  if (!isAxiosError(error)) {
    return undefined;
  }
  return error.response?.status;
};
